// This file is part of `styles.tsx`'s module-init graph (`styles.tsx` imports
// `TooltipIcon` from here). It must therefore stay strictly BELOW
// `adminComponents` in the dependency graph — if it reaches `adminComponents`,
// that pulls in `AdminEditor`, whose top-level `styled(Box)` runs before
// `styles.tsx` finishes defining `Box`, producing the webpack/HMR TDZ error
// "Cannot access 'Box' before initialization".
//
// Two things keep this file below `adminComponents`:
//   1. Every import below is a DIRECT source import, never the
//      `@vgrubs/components` main barrel (which re-exports `adminComponents`).
//   2. `useIsMobile` is INLINED below rather than imported from
//      `helpers/helpers.tsx`, because that file imports the `adminComponents`
//      barrel. This file is the hook's only app-wide consumer anyway.
import { Div } from '../../styles/styles';
import { H3, H5, P } from '../../styles/text';
import { InfoIcon } from '../../assets';
import { lightTheme } from '../../styles/theme';
import { useEffect, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import { createPortal } from 'react-dom';
import { InfoAnchor } from './Tooltips.styled';

const MOBILE_BREAKPOINT = 800;

// Inlined from helpers/helpers.tsx — see the import note above.
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);

    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches);
    };

    setIsMobile(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Safari < 14
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  return isMobile;
};

/**
 * Tooltip architecture
 * ─────────────────────
 *
 * `react-tooltip` is designed for *one (or a few) <Tooltip /> instances per
 * page, many anchors*. The previous pattern in this file did the opposite —
 * each wrapper mounted its own `<Tooltip />` pair (desktop + mobile) with a
 * `Math.random()` id regenerated on every render. With ~25 wrappers per row
 * × ~30 visible+overscan rows in a virtualized list, that produced ~1,500
 * Tooltip instances simultaneously, each registering global event listeners
 * and re-registering them every time the parent re-rendered. Scroll perf
 * died.
 *
 * Fixes applied here:
 *
 *   1. Stable IDs via `useId()` instead of `Math.random()` — even in the
 *      one wrapper that still needs a local Tooltip (TooltipWrapperWith-
 *      ComponentAndState, which uses the `render` prop for JSX content),
 *      the id is now stable across renders.
 *
 *   2. A single shared <GlobalTooltips /> mounted once per app at the
 *      layout root. The five string-content wrappers below just render an
 *      anchor with `data-tooltip-id="vg-app-tooltip"` + `data-tooltip-html`.
 *      `react-tooltip` reads the content from the active anchor's data
 *      attribute, so one Tooltip instance can serve every anchor in the
 *      app. The Delivery page went from ~1,500 Tooltip instances to 2.
 *
 *   3. <GlobalTooltips /> renders two Tooltips sharing the same id —
 *      one styled for desktop (clickable, max-width 500px) and one for
 *      mobile (max-width 95%), with the matching `$desktop`/`$mobile`
 *      Div wrappers using CSS media queries to keep only the right one
 *      visible per breakpoint. Same pattern as before, just hoisted.
 */

/** The one app-wide tooltip. Anything with data-tooltip-id set to this shows in it. */
export const TOOLTIP_ID = 'vg-app-tooltip';

const TooltipPortal = ({ children }: { children: React.ReactNode }) =>
  typeof document !== 'undefined'
    ? createPortal(children, document.body)
    : null;

/**
 * Mount this ONCE per app at the root (App.tsx / providerInner.tsx).
 * Every {Text,Title,SubTitle}WithTooltipIcon, TooltipIcon, and
 * TooltipWrapper in the app then point at this instance via
 * `data-tooltip-id="vg-app-tooltip"`.
 *
 * A single Tooltip serves both viewports; `useIsMobile()` listens to a
 * `matchMedia` query and re-renders this component on breakpoint cross,
 * so `maxWidth` swaps between desktop (500px) and mobile (95%) without
 * needing two parallel instances. `clickable` is enabled in both modes
 * so users can interact with tooltip content (e.g. copying text); the
 * previous mobile-only "non-clickable" behavior was inconsistent with
 * how desktop anchors that surface the same content already worked.
 */
export const GlobalTooltips = () => {
  const isMobile = useIsMobile();
  return (
    <TooltipPortal>
      <Tooltip
        clickable
        id={TOOLTIP_ID}
        positionStrategy="fixed"
        style={{
          maxWidth: isMobile ? '95%' : '500px',
          background: lightTheme.white,
          color: lightTheme.darkGray,
          padding: 16,
          borderRadius: 20,
          boxShadow: '0px 10px 15px rgba(0, 0, 0, 0.05)',
          zIndex: '99999',
          fontWeight: 400,
          textTransform: 'none',
          letterSpacing: 'normal',
          lineHeight: 1.4,
          textAlign: 'left',
        }}
        border="1px solid #e3e3e3"
      />
    </TooltipPortal>
  );
};

/**
 * Text with an icon glued to its last word.
 *
 * The text used to sit in an inline-flex row next to the icon. Once the text
 * wrapped, the whole paragraph became one flex item as wide as the row and the
 * icon was pushed to its right edge, away from the words (Delivery Assignment
 * Strategy on a phone). Now the text flows normally and only the last word
 * shares a no-wrap inline-flex box with the icon, so the icon follows wherever
 * the text ends and is still centered on that word, as `InfoAnchor` expects.
 */
const TextEndingInIcon = ({
  text,
  icon,
}: {
  text: string;
  icon: React.ReactNode;
}) => {
  if (!icon) return <>{text}</>;
  const split = text.trimEnd().lastIndexOf(' ');
  const head = split === -1 ? '' : text.slice(0, split + 1);
  const last = split === -1 ? text.trimEnd() : text.trimEnd().slice(split + 1);

  return (
    <>
      {head}
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          whiteSpace: 'nowrap',
          // The text styles paint a <span> in the brand red (their highlight).
          color: 'inherit',
        }}
      >
        {last}
        {icon}
      </span>
    </>
  );
};

export const TextWithTooltipIcon = ({
  text,
  tooltip,
  bold,
  color,
  textColor,
  icon,
  ...props
}: {
  text: string;
  tooltip?: string | undefined | false;
  bold?: boolean;
  color?: string;
  textColor?: string;
  /** Trailing glyph; defaults to InfoIcon. Pass e.g. <CopyIcon /> to override (see CopyTextWithIcon). */
  icon?: React.ReactNode;
}) => {
  return (
    <P
      $m="0"
      $bold={bold}
      style={{ display: 'inline-block' }}
      $color={textColor}
      {...props}
    >
      <TextEndingInIcon
        text={text}
        icon={
          tooltip && (
            <InfoAnchor
              data-tooltip-id={TOOLTIP_ID}
              data-tooltip-html={tooltip}
              data-tooltip-place="top"
              $color={color}
            >
              {icon ?? <InfoIcon />}
            </InfoAnchor>
          )
        }
      />
    </P>
  );
};

export const TitleWithTooltipIcon = ({
  text,
  tooltip,
  textColor,
  mb,
}: {
  text: string;
  tooltip: string;
  textColor: string;
  mb: string;
}) => {
  return (
    <H3 $color={textColor} $mb={mb} style={{ display: 'inline-block' }}>
      <TextEndingInIcon
        text={text}
        icon={
          <InfoAnchor
            data-tooltip-id={TOOLTIP_ID}
            data-tooltip-html={tooltip}
            data-tooltip-place="top"
          >
            <InfoIcon />
          </InfoAnchor>
        }
      />
    </H3>
  );
};

export const SubTitleWithTooltipIcon = ({
  text,
  tooltip,
  textColor,
  mb,
  color,
  maxWidth,
}: {
  text: string;
  tooltip?: string | undefined | false;
  textColor?: string;
  mb?: string;
  color?: string;
  maxWidth?: string;
}) => {
  return (
    <H5
      $color={textColor}
      $mb={mb}
      $left
      style={{ display: 'inline-block', maxWidth }}
    >
      <TextEndingInIcon
        text={text}
        icon={
          tooltip && (
            <InfoAnchor
              data-tooltip-id={TOOLTIP_ID}
              data-tooltip-html={tooltip}
              data-tooltip-place="top"
              $color={color}
              onClick={(e) => e.stopPropagation()}
            >
              <InfoIcon />
            </InfoAnchor>
          )
        }
      />
    </H5>
  );
};

/**
 * The info icon on its own, for a row that lays out its text itself. Put it
 * in a flex row with align-items: center and it lines up with the text; do
 * not give it (or a wrapper around it) a margin to nudge it - see
 * `InfoAnchor` for why that was never the fix.
 */
export const TooltipIcon = ({
  tooltip,
  color,
  p,
}: {
  tooltip: string;
  color?: string;
  /**
   * @deprecated Was used to override the mobile tooltip's `minWidth`.
   * Now applied via global tooltip style; this prop is retained for
   * backwards compatibility but has no effect.
   */
  minWidthSM?: string;
  /** Padding around the icon, which widens what can be hovered. */
  p?: string;
}) => (
  <InfoAnchor
    data-tooltip-id={TOOLTIP_ID}
    data-tooltip-html={tooltip}
    data-tooltip-place="top"
    $color={color}
    $p={p}
  >
    <InfoIcon />
  </InfoAnchor>
);

export const TooltipWrapper = ({
  tooltip,
  children,
  $maxWidth,
  $width,
  copy,
  onClick,
  style,
}: {
  tooltip?: string | undefined | false;
  children: React.ReactNode;
  $maxWidth?: string;
  $width?: string;
  copy?: string;
  onClick?: () => void;
  /**
   * @deprecated Custom per-instance tooltip widths are no longer
   * supported (single global tooltip serves all anchors). Use the global
   * style if a different width is needed app-wide.
   */
  tooltipWidth?: string;
  tooltipWidthSM?: string;
  style?: React.CSSProperties;
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(copy || '');
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 1000);
  };

  return (
    <div
      style={{
        display: 'inline-block',
        maxWidth: $maxWidth || '100%',
        width: $width || '',
        ...style,
      }}
    >
      <a
        data-tooltip-id={TOOLTIP_ID}
        data-tooltip-html={copied ? 'Copied!' : tooltip || ''}
        data-tooltip-place="top"
        style={{
          color: lightTheme.darkGray,
          maxWidth: $maxWidth || '100%',
          textDecoration: copy ? 'underline' : 'none',
          verticalAlign: 'middle',
        }}
        onClick={(e) => {
          if (copy) {
            e.stopPropagation();
            handleCopy();
          }
          if (onClick) {
            e.stopPropagation();
            onClick();
          }
        }}
      >
        {children}
      </a>
    </div>
  );
};

/**
 * Stays as a per-instance tooltip because it renders JSX content via the
 * `render` prop and tracks its own open/closed state — neither of which
 * works through the global `data-tooltip-html` attribute-based pattern.
 *
 * IDs are still stabilized via the `id` prop the caller supplies (no
 * Math.random()), so this one no longer mounts/remounts on every parent
 * render.
 */
export const TooltipWrapperWithComponentAndState = ({
  tooltip,
  children,
  $maxWidth,
  $width,
  tooltipWidth,
  tooltipWidthSM,
  style,
  afterShow,
  id,
}: {
  tooltip?: React.ReactNode;
  children: React.ReactNode;
  $maxWidth?: string;
  $width?: string;
  tooltipWidth?: string;
  tooltipWidthSM?: string;
  style?: React.CSSProperties;
  afterShow?: () => void;
  id: string;
}) => {
  const [isOpened, setIsOpened] = useState(false);
  const tooltipIsString = typeof tooltip === 'string';

  return (
    <div
      style={{
        display: 'inline-block',
        maxWidth: $maxWidth || '100%',
        width: $width || '100%',
        ...style,
      }}
    >
      <a
        data-tooltip-id={id}
        data-tooltip-html={tooltipIsString ? (tooltip as string) : undefined}
        data-tooltip-place="top"
        style={{
          color: lightTheme.darkGray,
          maxWidth: $maxWidth || '100%',
          verticalAlign: 'middle',
        }}
        onMouseEnter={() => {
          if (!isOpened) {
            setIsOpened(true);
            afterShow?.();
          }
        }}
        onMouseLeave={() => {
          if (isOpened) {
            setIsOpened(false);
          }
        }}
        onFocus={() => {
          if (!isOpened) {
            setIsOpened(true);
            afterShow?.();
          }
        }}
        onBlur={() => {
          if (isOpened) {
            setIsOpened(false);
          }
        }}
      >
        {children}
      </a>
      <TooltipPortal>
        <Div $desktop>
          <Tooltip
            clickable
            id={id}
            isOpen={isOpened}
            setIsOpen={setIsOpened}
            render={tooltip ? () => tooltip : undefined}
            style={{
              maxWidth: tooltipWidth || '500px',
              background: lightTheme.white,
              color: lightTheme.darkGray,
              padding: 16,
              borderRadius: 20,
              boxShadow: '0px 10px 15px rgba(0, 0, 0, 0.05)',
              zIndex: '99999',
            }}
            border="1px solid #e3e3e3"
          />
        </Div>
        <Div $mobile>
          <Tooltip
            id={id}
            isOpen={isOpened}
            setIsOpen={setIsOpened}
            render={tooltip ? () => tooltip : undefined}
            style={{
              maxWidth: tooltipWidthSM || '95%',
              background: lightTheme.white,
              color: lightTheme.darkGray,
              padding: 16,
              borderRadius: 20,
              boxShadow: '0px 10px 15px rgba(0, 0, 0, 0.05)',
              zIndex: '99999',
            }}
            border="1px solid #e3e3e3"
          />
        </Div>
      </TooltipPortal>
    </div>
  );
};
